import { createTheme, alpha } from '@mui/material/styles';
import { palette } from './palette';
import { typography } from './typography';
import { STANDARD_INPUT_HEIGHT } from './constants';

/**
 * TreePlotter Design System Theme
 *
 * Comprehensive theme configuration including:
 * - Standard MUI palette colors (primary, secondary, info, success, warning, error)
 * - Custom palette extensions (qualitative, diverging, achromatopsia, landcover)
 * - Complete typography system
 * - Component style overrides for consistent design
 */
export const theme = createTheme({
  palette: palette(),
  typography,
  shape: { borderRadius: 0 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: '0.875rem',
          padding: '0.75rem 1.5rem',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid',
          borderColor: alpha(palette().text.secondary, 0.24),
          boxShadow: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
        },
        standardSuccess: {
          backgroundColor: alpha(palette().success.main, 0.1),
          color: palette().success.main,
          border: `1px solid ${alpha(palette().success.main, 0.3)}`,
        },
        standardError: {
          backgroundColor: alpha(palette().error.main, 0.1),
          color: palette().error.main,
          border: `1px solid ${alpha(palette().error.main, 0.3)}`,
        },
        standardWarning: {
          backgroundColor: alpha(palette().warning.main, 0.1),
          color: palette().warning.dark,
          border: `1px solid ${alpha(palette().warning.main, 0.3)}`,
        },
        standardInfo: {
          backgroundColor: alpha(palette().info.main, 0.1),
          color: palette().info.main,
          border: `1px solid ${alpha(palette().info.main, 0.3)}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiAutocomplete: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { minHeight: STANDARD_INPUT_HEIGHT },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        // 12px label inside the 40px box: MUI's small transform sits ~2px above center
        outlined: ({ ownerState }) =>
          ownerState.size === 'small' && !ownerState.shrink
            ? { transform: 'translate(14px, 11px) scale(1)' }
            : {},
      },
    },
  },
});
